import { Stack, useTheme, CircularProgress, Box } from "@mui/material";
import { FormProvider, useForm, useFormContext, useWatch } from "react-hook-form";
import { FormInput } from "src/components/FormInput";
import { zodResolver } from "@hookform/resolvers/zod";
import { Fragment, useId, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "react-query";
import { Banner, Button, Typography, WhiteBox } from "@foodpilot/foods";
import { z } from "zod";
import { loginUserFn } from "src/api/authApi";
import { disableRefreshToken } from "src/_Globals/queryClient/queryClient";
import {
    buildForgotPasswordUrl,
    forgetPendingSsoDestination,
    isSafeDestination,
    parseLoginReason,
} from "src/services/loginEntry";
import { SsoConnectorButton } from "src/components/SsoConnectorButton";
import { useSsoDance } from "src/utils/useSsoDance.ts";
import { resolveLoginLayout, SsoConnector } from "./ssoConnector";
import { useSsoConnector } from "./useSsoConnector";

export const loginSchema = z.object({
    email: z.string().min(1, "Email address is required").email({ message: "Email Address is invalid" }),
    password: z
        .string()
        .min(1, "Password is required")
        // .min(8, "Password must be more than 8 characters")
        .max(32, "Password must be less than 32 characters"),
});
export type LoginInput = z.infer<typeof loginSchema>;

export const Login = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const [isError, setError] = useState(false);
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const location = useLocation();
    const urlSearchParams = new URLSearchParams(location.search);
    const rawDestination = urlSearchParams.get("destination");
    const destination = isSafeDestination(rawDestination) ? rawDestination : null;

    const { connectors, loginMode, isLoading: isSsoConnectorLoading } = useSsoConnector();
    const [isCredentialsFormOpen, setCredentialsFormOpen] = useState(false);
    const credentialsFormId = useId();

    const { displayCredentialsForm, isSsoFirst, soleConnector } = resolveLoginLayout({
        connectors,
        isCredentialsFormOpen,
        isLoading: isSsoConnectorLoading,
        loginMode,
    });

    const methods = useForm<LoginInput>({
        defaultValues: {
            email: "",
            password: "",
        },
        resolver: zodResolver(loginSchema),
    });

    const mutateLogin = useMutation({
        meta: {
            disableRefreshToken,
        },
        mutationFn: (notLoggedUser: LoginInput) => {
            return loginUserFn(notLoggedUser);
        },
        onSuccess: () => {
            forgetPendingSsoDestination();
            queryClient.resetQueries();

            navigate(destination ?? "/");
        },
        onError: (err, _variables, _context) => {
            setError(true);
        },
    });

    return (
        <FormProvider {...methods}>
            <WhiteBox
                padding="40px"
                paddingTop="32px"
                boxShadow="0 2px 8px 0 rgba(42, 43, 39, 0.04)"
                position="relative"
            >
                {mutateLogin.isLoading && (
                    <Stack
                        sx={{
                            background: "rgba(255, 255, 255, 0.5)",
                            position: "absolute",
                            inset: "calc(40px / 2)",
                            alignItems: "center",
                            justifyContent: "center",
                            zIndex: 1,
                        }}
                    >
                        <CircularProgress />
                    </Stack>
                )}
                <Stack gap={theme.spacing(4.5)}>
                    <LoginEntryBanner reason={urlSearchParams.get("reason")} />
                    <LoginHeader connector={isSsoFirst ? soleConnector : null} />
                    {isSsoFirst && (
                        <Stack gap={theme.spacing(2)}>
                            {connectors.map((ssoConnector, index) => (
                                <SsoConnectorAction
                                    key={`${ssoConnector.protocol}/${ssoConnector.providerName}`}
                                    connector={ssoConnector}
                                    destination={destination}
                                    variant={index === 0 ? "primary" : "secondary"}
                                />
                            ))}
                            <Button
                                variant="text"
                                onClick={() => setCredentialsFormOpen((isOpen) => isOpen === false)}
                                aria-expanded={isCredentialsFormOpen}
                                aria-controls={credentialsFormId}
                                sx={{ alignSelf: "center", color: theme.custom.grey[2000] }}
                            >
                                {isCredentialsFormOpen ?
                                    t("login.sso.hide_password_login", "Hide")
                                :   t("login.sso.password_login", "Log in with a password")}
                            </Button>
                        </Stack>
                    )}
                    {isSsoFirst && isCredentialsFormOpen && <LabelledSeparator label={t("login.sso.or", "or")} />}
                    <Stack
                        id={credentialsFormId}
                        component="form"
                        onSubmit={methods.handleSubmit(
                            (data, _event) => {
                                setError(false);
                                mutateLogin.mutate(data);
                            },
                            (_errors, _event) => {
                                // Here are the errors from the argument formatting.
                                // We may want to display the Errors using the zod Schema
                                // at some point;
                                setError(true);
                            },
                        )}
                        noValidate
                        autoComplete="off"
                        sx={{ display: displayCredentialsForm ? "flex" : "none", gap: theme.spacing(4.5) }}
                    >
                        {isError && (
                            <Typography variant="h4" align="center" color="error">
                                {t("incorrect_email_or_password")}
                            </Typography>
                        )}
                        <LoginInputs destination={destination} isSsoFirst={isSsoFirst} />
                    </Stack>
                    {connectors.length > 0 && isSsoFirst === false && (
                        <SsoConnectorSection connectors={connectors} destination={destination} />
                    )}
                </Stack>
            </WhiteBox>
        </FormProvider>
    );
};

type LoginEntryBannerProps = {
    reason: string | null;
};
const LoginEntryBanner = (props: LoginEntryBannerProps) => {
    const { t } = useTranslation();
    const reason = parseLoginReason(props.reason);

    if (reason === null) {
        return null;
    }

    return (
        <Banner variant="info">
            {reason === "expired" ?
                t("login.entry.expired", "Your session has expired. Log in again to pick up where you left off.")
            :   t("login.entry.logout", "You have been logged out.")}
        </Banner>
    );
};

type LoginHeaderProps = {
    connector: SsoConnector | null;
};
const LoginHeader = (props: LoginHeaderProps) => {
    const { connector } = props;
    const theme = useTheme();
    const { t } = useTranslation();

    return (
        <Stack justifyContent="center" alignItems="center" gap={theme.spacing(2)}>
            <Typography variant="h2" align="center">
                {t("login_title")}
            </Typography>
            <Typography variant="body" align="center" sx={{ color: theme.custom.grey[2000] }}>
                {connector === null ?
                    t("login_info")
                :   t("login.sso.header_info", "Use your {{connector}} account to access the platform.", {
                        connector: connector.label,
                    })
                }
            </Typography>
        </Stack>
    );
};

type LabelledSeparatorProps = {
    label: string;
};
const LabelledSeparator = (props: LabelledSeparatorProps) => {
    const theme = useTheme();
    const line = {
        flex: 1,
        height: theme.spacing(0.125),
        backgroundColor: theme.custom.grey[500],
    };

    return (
        <Stack flexDirection="row" alignItems="center" gap={theme.spacing(1.25)}>
            <Box sx={line} />
            <Typography variant="body-medium" sx={{ color: theme.custom.grey[2000] }}>
                {props.label}
            </Typography>
            <Box sx={line} />
        </Stack>
    );
};

type SsoConnectorSectionProps = {
    connectors: SsoConnector[];
    destination: string | null;
};
const SsoConnectorSection = (props: SsoConnectorSectionProps) => {
    const { connectors, destination } = props;
    const { t } = useTranslation();
    const theme = useTheme();

    return (
        <Stack gap={theme.spacing(2)}>
            <LabelledSeparator label={t("login.sso.continue_with", "Or continue with:")} />
            {connectors.map((connector) => (
                <SsoConnectorAction
                    key={`${connector.protocol}/${connector.providerName}`}
                    connector={connector}
                    destination={destination}
                    variant="secondary"
                />
            ))}
        </Stack>
    );
};

type SsoConnectorActionProps = {
    connector: SsoConnector;
    destination: string | null;
    variant: "primary" | "secondary";
};
const SsoConnectorAction = (props: SsoConnectorActionProps) => {
    const { connector, destination, variant } = props;
    const { t } = useTranslation();
    const theme = useTheme();
    const { getAuth, loading, error } = useSsoDance();

    return (
        <Fragment>
            <Stack role="alert" sx={{ "&:empty": { display: "none" }, marginBottom: theme.spacing(2) }}>
                {error !== null && (
                    <Banner variant="error">{t("login.sso.error", "Single sign-on failed. Please try again.")}</Banner>
                )}
            </Stack>
            <SsoConnectorButton
                connector={connector}
                label={
                    variant === "primary" ?
                        t("login.sso.continue_with_connector", "Continue with {{connector}}", {
                            connector: connector.label,
                        })
                    :   connector.label
                }
                variant={variant}
                disabled={loading}
                onClick={() => getAuth(connector.authorizationUrl, connector.providerName, destination)}
            />
        </Fragment>
    );
};

type LoginInputsProps = {
    destination: string | null;
    isSsoFirst: boolean;
};
const LoginInputs = (props: LoginInputsProps) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const { destination, isSsoFirst } = props;
    const { control } = useFormContext<LoginInput>();
    const values = useWatch({ control });

    return (
        <Stack gap={theme.spacing(3)}>
            <FormInput
                type="email"
                name="email"
                label={t("adresse_email")}
                inputProps={{
                    "data-testid": "test-mailId",
                    autoComplete: "email",
                }}
            />

            <Stack>
                <FormInput
                    type="password"
                    name="password"
                    label={t("mot_de_passe")}
                    inputProps={{
                        "data-testid": "test-pwdId",
                        autoComplete: "current-password",
                    }}
                />
                <Button
                    variant="text"
                    component={Link}
                    to={buildForgotPasswordUrl({ destination })}
                    sx={{
                        width: "100%",
                        height: "auto",
                        justifyContent: "left",
                    }}
                >
                    {t("forgotten_password")}
                </Button>
            </Stack>

            <Button
                variant={isSsoFirst ? "secondary" : "primary"}
                sx={{ width: "100%", fontWeight: 700 }}
                type="submit"
                disabled={!values.email || !values.password}
            >
                {t("login_connect")}
            </Button>
        </Stack>
    );
};
